import React, { useState, useEffect } from 'react';
import { useProgress } from '../../../context/ProgressContext';
import { SkillLevels, PieceMoveSkills, AgentRank } from '../../../types/progress';
import { getAllMissions } from '../../../data/missions';
import { getPerformanceMetrics, clearPerformanceMetrics, logPerformanceMetrics } from '../../../utils/performanceMonitor';
import styles from './DebugPanel.module.css';

/**
 * Debug panel for developers to modify game state directly
 * This is not intended for end users
 */
const DebugPanel: React.FC = () => {
  const { userProgress, updateProgress } = useProgress();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'skills' | 'missions' | 'stages' | 'stats' | 'performance'>('skills');

  // All missions in the game
  const [allMissions] = useState(getAllMissions());

  // State for all skill values
  const [skillValues, setSkillValues] = useState<SkillLevels>({
    ...userProgress.skillLevels
  });

  // State for stage value
  const [stageValue, setStageValue] = useState(userProgress.currentStage);

  // State for completed missions
  const [completedMissions, setCompletedMissions] = useState<string[]>([...userProgress.completedMissions]);

  // State for stats
  const [statistics, setStatistics] = useState({...userProgress.statistics});
  
  // State for component ID to profile
  const [componentToProfile, setComponentToProfile] = useState('AnalyticsDashboard');

  // Update local state when user progress changes
  useEffect(() => {
    if (isOpen) {
      setSkillValues({...userProgress.skillLevels});
      setStageValue(userProgress.currentStage);
      setCompletedMissions([...userProgress.completedMissions]);
      setStatistics({...userProgress.statistics});
    }
  }, [isOpen, userProgress.completedMissions, userProgress.currentStage, userProgress.skillLevels, userProgress.statistics]);

  // Function to toggle the panel
  const togglePanel = () => {
    setIsOpen(!isOpen);
  };

  // Function to handle skill value changes
  const handleSkillChange = (skillName: keyof SkillLevels, value: number) => {
    setSkillValues(prev => ({
      ...prev,
      [skillName]: value
    }));
  };

  // Function to handle piece move skill changes
  const handlePieceMoveSkillChange = (piece: keyof PieceMoveSkills, value: number) => {
    const currentPieceMoves = skillValues.pieceMoves || {
      pawn: 1,
      knight: 1,
      bishop: 1,
      rook: 1,
      queen: 1,
      king: 1
    };

    setSkillValues(prev => ({
      ...prev,
      pieceMoves: {
        ...currentPieceMoves,
        [piece]: value
      }
    }));
  };

  // Function to handle stage value change
  const handleStageChange = (value: number) => {
    setStageValue(value);
  };

  // Function to handle mission completion toggle
  const handleMissionToggle = (missionId: string) => {
    setCompletedMissions(prev => {
      if (prev.includes(missionId)) {
        return prev.filter(id => id !== missionId);
      } else {
        return [...prev, missionId];
      }
    });
  };

  // Function to save changes
  const saveChanges = () => {
    updateProgress({
      skillLevels: skillValues,
      currentStage: stageValue,
      completedMissions: completedMissions,
      statistics: statistics
    });
    alert('Debug changes saved!');
  };

  // Function to reset all skills to level 1
  const resetSkills = () => {
    const resetValues: SkillLevels = {
      coordinates: 1,
      basicMoves: 1,
      sequences: 1,
      gameAnalysis: 1,
      castling: 1,
      pawnPromotion: 1,
      enPassant: 1,
      pieceMoves: {
        pawn: 1,
        knight: 1,
        bishop: 1,
        rook: 1,
        queen: 1,
        king: 1
      }
    };
    
    setSkillValues(resetValues);
  };

  // Function to max out all skills (for testing end-game scenarios)
  const maxSkills = () => {
    const maxValues: SkillLevels = {
      coordinates: 10,
      basicMoves: 10,
      sequences: 10,
      gameAnalysis: 10,
      castling: 10,
      pawnPromotion: 10,
      enPassant: 10,
      pieceMoves: {
        pawn: 10,
        knight: 10,
        bishop: 10,
        rook: 10,
        queen: 10,
        king: 10
      }
    };
    
    setSkillValues(maxValues);
  };

  // Function to unlock all missions
  const unlockAllMissions = () => {
    const allMissionIds = allMissions.map(mission => mission.id);
    setCompletedMissions(allMissionIds);
  };

  // Function to unlock missions up to a specific stage
  const unlockMissionsToStage = (stage: number) => {
    const stageMissions = allMissions
      .filter(mission => mission.stage <= stage)
      .map(mission => mission.id);
    
    setCompletedMissions(stageMissions);
  };

  // Function to update stage with appropriate skill levels and missions
  const setStageWithProgressValues = (stage: number) => {
    // Set stage
    setStageValue(stage);
    
    // Unlock missions for this stage and below
    unlockMissionsToStage(stage - 1); // Unlock previous stages completely
    
    // Boost skills based on stage
    const skillLevel = Math.min(10, stage * 2);
    setSkillValues(prev => ({
      ...prev,
      coordinates: skillLevel,
      basicMoves: skillLevel,
      sequences: Math.max(1, skillLevel - 2),
      gameAnalysis: Math.max(1, skillLevel - 3)
    }));
    
    // Update statistics
    setStatistics(prev => ({
      ...prev,
      totalMissionsCompleted: completedMissions.length,
      totalChallengesCompleted: completedMissions.length * 3 // Rough estimate
    }));
  };

  // Performance monitoring handlers
  const handleComponentToProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setComponentToProfile(e.target.value);
  };

  const handleProfileComponent = () => {
    logPerformanceMetrics(componentToProfile);
  };

  const handleClearMetrics = () => {
    clearPerformanceMetrics();
    alert('Performance metrics cleared');
  };

  return (
    <div className={styles.debugContainer}>
      {/* Debug toggle button */}
      <button 
        className={styles.debugToggle}
        onClick={togglePanel}
        title="Developer Debug Mode"
      >
        🛠️
      </button>

      {/* Debug panel */}
      {isOpen && (
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <h3>Developer Debug Panel</h3>
            <button onClick={togglePanel} className={styles.closeButton}>×</button>
          </div>

          {/* Tab navigation */}
          <div className={styles.tabBar}>
            <button 
              className={`${styles.tabButton} ${activeTab === 'skills' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('skills')}
            >
              Skills
            </button>
            <button 
              className={`${styles.tabButton} ${activeTab === 'missions' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('missions')}
            >
              Missions
            </button>
            <button 
              className={`${styles.tabButton} ${activeTab === 'stages' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('stages')}
            >
              Stages
            </button>
            <button 
              className={`${styles.tabButton} ${activeTab === 'stats' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('stats')}
            >
              Stats
            </button>
            <button 
              className={`${styles.tabButton} ${activeTab === 'performance' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('performance')}
            >
              Performance
            </button>
          </div>
          
          {/* Skills tab */}
          {activeTab === 'skills' && (
            <div className={styles.skillsContainer}>
              <h4>Basic Skills</h4>
              <div className={styles.skillRow}>
                <label htmlFor="skill-coordinates">Coordinates:</label>
                <input
                  id="skill-coordinates"
                  type="number"
                  min="1"
                  max="10"
                  value={skillValues.coordinates}
                  onChange={(e) => handleSkillChange('coordinates', parseInt(e.target.value))}
                />
              </div>
              <div className={styles.skillRow}>
                <label htmlFor="skill-basicMoves">Basic Moves:</label>
                <input
                  id="skill-basicMoves"
                  type="number"
                  min="1"
                  max="10"
                  value={skillValues.basicMoves}
                  onChange={(e) => handleSkillChange('basicMoves', parseInt(e.target.value))}
                />
              </div>
              <div className={styles.skillRow}>
                <label htmlFor="skill-sequences">Sequences:</label>
                <input
                  id="skill-sequences"
                  type="number"
                  min="1"
                  max="10"
                  value={skillValues.sequences}
                  onChange={(e) => handleSkillChange('sequences', parseInt(e.target.value))}
                />
              </div>
              <div className={styles.skillRow}>
                <label htmlFor="skill-gameAnalysis">Game Analysis:</label>
                <input
                  id="skill-gameAnalysis"
                  type="number"
                  min="1"
                  max="10"
                  value={skillValues.gameAnalysis}
                  onChange={(e) => handleSkillChange('gameAnalysis', parseInt(e.target.value))}
                />
              </div>

              <h4>Special Move Skills</h4>
              <div className={styles.skillRow}>
                <label htmlFor="skill-castling">Castling:</label>
                <input
                  id="skill-castling"
                  type="number"
                  min="1"
                  max="10"
                  value={skillValues.castling || 1}
                  onChange={(e) => handleSkillChange('castling', parseInt(e.target.value))}
                />
              </div>
              <div className={styles.skillRow}>
                <label htmlFor="skill-pawnPromotion">Pawn Promotion:</label>
                <input
                  id="skill-pawnPromotion"
                  type="number"
                  min="1"
                  max="10"
                  value={skillValues.pawnPromotion || 1}
                  onChange={(e) => handleSkillChange('pawnPromotion', parseInt(e.target.value))}
                />
              </div>
              <div className={styles.skillRow}>
                <label htmlFor="skill-enPassant">En Passant:</label>
                <input
                  id="skill-enPassant"
                  type="number"
                  min="1"
                  max="10"
                  value={skillValues.enPassant || 1}
                  onChange={(e) => handleSkillChange('enPassant', parseInt(e.target.value))}
                />
              </div>

              <h4>Piece Move Skills</h4>
              {Object.entries(skillValues.pieceMoves || {
                pawn: 1,
                knight: 1,
                bishop: 1,
                rook: 1,
                queen: 1,
                king: 1
              }).map(([piece, level]) => (
                <div className={styles.skillRow} key={piece}>
                  <label htmlFor={`skill-piece-${piece}`}>
                    {piece.charAt(0).toUpperCase() + piece.slice(1)}:
                  </label>
                  <input
                    id={`skill-piece-${piece}`}
                    type="number"
                    min="1"
                    max="10"
                    value={level}
                    onChange={(e) => handlePieceMoveSkillChange(
                      piece as keyof PieceMoveSkills,
                      parseInt(e.target.value)
                    )}
                  />
                </div>
              ))}

              <div className={styles.buttonContainer}>
                <button onClick={resetSkills} className={styles.resetButton}>
                  Reset All Skills
                </button>
                <button onClick={maxSkills} className={styles.maxButton}>
                  Max All Skills
                </button>
              </div>
            </div>
          )}

          {/* Missions tab */}
          {activeTab === 'missions' && (
            <div className={styles.missionsContainer}>
              <h4>Mission Completion</h4>
              <div className={styles.missionList} data-testid="mission-list">
                {allMissions.map(mission => (
                  <div key={mission.id} className={styles.missionRow}>
                    <label htmlFor={`mission-${mission.id}`} className={styles.missionLabel}>
                      <input
                        id={`mission-${mission.id}`}
                        type="checkbox"
                        checked={completedMissions.includes(mission.id)}
                        onChange={() => handleMissionToggle(mission.id)}
                      />
                      <div className={styles.missionInfo}>
                        <span className={styles.missionTitle}>{mission.title}</span>
                        <span className={styles.missionDetails}>
                          Stage {mission.stage} | {mission.type} | Diff: {mission.difficulty}
                        </span>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
              <div className={styles.buttonContainer}>
                <button 
                  onClick={() => setCompletedMissions([])} 
                  className={styles.resetButton}
                >
                  Reset All Missions
                </button>
                <button 
                  onClick={unlockAllMissions} 
                  className={styles.maxButton}
                >
                  Unlock All Missions
                </button>
              </div>
            </div>
          )}

          {/* Stages tab */}
          {activeTab === 'stages' && (
            <div className={styles.stageContainer}>
              <h4>Game Progression</h4>
              <div className={styles.skillRow}>
                <label htmlFor="currentStage">Current Stage:</label>
                <input
                  id="currentStage"
                  type="number"
                  min="1"
                  max="5"
                  value={stageValue}
                  onChange={(e) => handleStageChange(parseInt(e.target.value))}
                />
              </div>
              
              <div className={styles.stagePresets}>
                <h4>Quick Setup Presets</h4>
                <div className={styles.presetButtons}>
                  {[1, 2, 3, 4, 5].map(stage => (
                    <button
                      key={stage}
                      onClick={() => setStageWithProgressValues(stage)}
                      className={`${styles.presetButton} ${stageValue === stage ? styles.activePreset : ''}`}
                    >
                      Stage {stage}
                    </button>
                  ))}
                </div>
                <div className={styles.presetDescription}>
                  <p>These presets will set the stage, unlock appropriate missions, and set skill levels.</p>
                </div>
              </div>

              <div className={styles.rankInfo}>
                <h4>Agent Ranks</h4>
                <div className={styles.rankRow}>
                  <span className={styles.rankName}>{AgentRank.ROOKIE}</span>
                  <span className={styles.rankRequirements}>Avg. skill: &lt;3, Missions: &lt;5</span>
                </div>
                <div className={styles.rankRow}>
                  <span className={styles.rankName}>{AgentRank.FIELD}</span>
                  <span className={styles.rankRequirements}>Avg. skill: ≥3, Missions: ≥5</span>
                </div>
                <div className={styles.rankRow}>
                  <span className={styles.rankName}>{AgentRank.SPECIAL}</span>
                  <span className={styles.rankRequirements}>Avg. skill: ≥5, Missions: ≥10</span>
                </div>
                <div className={styles.rankRow}>
                  <span className={styles.rankName}>{AgentRank.MASTER}</span>
                  <span className={styles.rankRequirements}>Avg. skill: ≥8, Missions: ≥15</span>
                </div>
              </div>
            </div>
          )}

          {/* Stats tab */}
          {activeTab === 'stats' && (
            <div className={styles.statsContainer}>
              <h4>Game Statistics</h4>
              <div className={styles.skillRow}>
                <label htmlFor="stats-totalMissions">Total Missions Completed:</label>
                <input
                  id="stats-totalMissions"
                  type="number"
                  min="0"
                  value={statistics.totalMissionsCompleted}
                  onChange={(e) => setStatistics(prev => ({
                    ...prev,
                    totalMissionsCompleted: parseInt(e.target.value)
                  }))}
                />
              </div>
              <div className={styles.skillRow}>
                <label htmlFor="stats-totalChallenges">Total Challenges Completed:</label>
                <input
                  id="stats-totalChallenges"
                  type="number"
                  min="0"
                  value={statistics.totalChallengesCompleted}
                  onChange={(e) => setStatistics(prev => ({
                    ...prev,
                    totalChallengesCompleted: parseInt(e.target.value)
                  }))}
                />
              </div>
              <div className={styles.skillRow}>
                <label htmlFor="stats-accuracy">Average Accuracy (%):</label>
                <input
                  id="stats-accuracy"
                  type="number"
                  min="0"
                  max="100"
                  value={Math.round(statistics.averageAccuracy * 100)}
                  onChange={(e) => setStatistics(prev => ({
                    ...prev,
                    averageAccuracy: parseInt(e.target.value) / 100
                  }))}
                />
              </div>
              <div className={styles.skillRow}>
                <label htmlFor="stats-bestScore">Best Score:</label>
                <input
                  id="stats-bestScore"
                  type="number"
                  min="0"
                  value={statistics.bestScore}
                  onChange={(e) => setStatistics(prev => ({
                    ...prev,
                    bestScore: parseInt(e.target.value)
                  }))}
                />
              </div>
            </div>
          )}

          {/* Performance tab */}
          {activeTab === 'performance' && (
            <div className={styles.performanceContainer}>
              <h4>Performance Monitoring</h4>
              
              <div className={styles.performanceSection}>
                <h5>Profile Component</h5>
                <div className={styles.performanceControls}>
                  <div className={styles.inputRow}>
                    <label htmlFor="component-id">Component ID:</label>
                    <input
                      id="component-id"
                      type="text"
                      value={componentToProfile}
                      onChange={handleComponentToProfileChange}
                      placeholder="e.g., AnalyticsDashboard"
                    />
                  </div>
                  
                  <div className={styles.buttonsRow}>
                    <button 
                      className={styles.profileButton}
                      onClick={handleProfileComponent}
                    >
                      Log Metrics
                    </button>
                    
                    <button 
                      className={styles.clearButton}
                      onClick={handleClearMetrics}
                    >
                      Clear Metrics
                    </button>
                  </div>
                </div>
              </div>
              
              <div className={styles.performanceSection}>
                <h5>Quick Profile</h5>
                <div className={styles.quickButtons}>
                  <button 
                    className={styles.quickProfileButton}
                    onClick={() => logPerformanceMetrics('AnalyticsDashboard')}
                  >
                    Analytics Dashboard
                  </button>
                  <button 
                    className={styles.quickProfileButton}
                    onClick={() => logPerformanceMetrics('DailyTrainingPage')}
                  >
                    Daily Training
                  </button>
                </div>
              </div>
              
              <div className={styles.performanceHint}>
                <p>Open the browser console (F12) to view performance metrics</p>
              </div>
            </div>
          )}
          
          {/* Save changes button */}
          {activeTab !== 'performance' && (
            <div className={styles.saveButtonContainer}>
              <button onClick={saveChanges} className={styles.saveButton}>
                Save All Changes
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DebugPanel; 